import { type ReactNode, createContext, useCallback, useContext, useState } from "react";
import { type IMenuSelectChallengesProps, MenuSelectChallenges } from "./index";

export type TOpenMenuSelectChallenges = (
  offer: IMenuSelectChallengesProps["offer"],
  anchorEl: Exclude<IMenuSelectChallengesProps["anchorEl"], null>,
  type: Exclude<IMenuSelectChallengesProps["type"], undefined>,
  onConfirm: IMenuSelectChallengesProps["onConfirm"],
  onClose?: IMenuSelectChallengesProps["onClose"]
) => void;

export interface IMenuSelectChallengesState extends Omit<IMenuSelectChallengesProps, "offer" | "onClose"> {
  offer: IMenuSelectChallengesProps["offer"] | null;
  onClose?: IMenuSelectChallengesProps["onClose"];
}

export interface IMenuSelectChallengesProviderProps {
  children: ReactNode | ReactNode[];
}

const Context = createContext<TOpenMenuSelectChallenges>(() => undefined);

export const useMenuSelectChallengesContext = () => useContext(Context);

export const MenuSelectChallengesProvider = ({ children }: IMenuSelectChallengesProviderProps) => {
  const [state, setState] = useState<IMenuSelectChallengesState>({
    open: false,
    offer: null,
    anchorEl: null,
    type: "propose",
    onConfirm: () => undefined,
  });

  const openMenuSelectChallenges: TOpenMenuSelectChallenges = useCallback(
    (offer, anchorEl, type, onConfirm, onClose) => {
      setState({
        open: true,
        offer,
        anchorEl,
        type,
        onConfirm,
        onClose,
      });
    },
    []
  );

  return (
    <Context.Provider value={openMenuSelectChallenges}>
      {children}
      {state.offer && state.onConfirm && (
        <MenuSelectChallenges
          open={state.open}
          offer={state.offer}
          anchorEl={state.anchorEl}
          type={state.type}
          onConfirm={state.onConfirm}
          onClose={() => {
            if (state.onClose) {
              state.onClose();
            }
            setState((state) => ({ ...state, open: false }));
          }}
        />
      )}
    </Context.Provider>
  );
};
